import { PkCarousel } from '@uefadigital/panenka-react';
import type { SyntheticEvent } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';

import type { MediaImage } from '@cobalt/utilities';
import { useWindowSize } from '@hooks/useWindowSize';
import Modal from '@shared/Modal';
import PictureHelper from '@shared/PictureHelper';
import { PkTheme } from '@shared-types/common.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { competitionUrl } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const imgFormats = ['0@w1'];

const FullScreen = ({
  showModal,
  photos,
  title,
  sponsorName,
  currentSlide,
  closeModal,
}: {
  showModal: boolean;
  photos: MediaImage[];
  title: string;
  sponsorName: string;
  currentSlide: number;
  closeModal: () => void;
}) => {
  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (!showModal) return;
    GoogleTagManagerHelper.addVirtualPage({
      virtPath: `/${competitionUrl}/photogallery/${photos.at(0).id}-${photos.length}`,
      pageType: 'gallery',
      pageSubType: 'photo gallery',
    });
  }, [photos, showModal]);

  const scrollToImage = useCallback(
    (index: number) => (ev: SyntheticEvent<HTMLImageElement, Event>) => {
      const el = ev.currentTarget;
      if (!el || index !== currentSlide) return;
      el.scrollIntoView({ block: 'center' });
    },
    [currentSlide],
  );

  const trackSlide = (category: 'Swipe' | 'Click', action: 'Previous image' | 'Next image') => () => {
    GoogleTagManagerHelper.ga4Event('photo_detail', { section_reference: 'photo_detail', interaction: action });
  };

  const modalContentMobile = useMemo(
    () => (
      <>
        {photos?.map((photo, i) => (
          <div key={`photo-${photo.id}`} className={styles.slide}>
            <PictureHelper
              formats={imgFormats}
              image={photo.image}
              title={photo.title}
              wrapperClassName={styles.img}
              onLoad={scrollToImage(i)}
            />
          </div>
        ))}
      </>
    ),
    [photos, scrollToImage],
  );

  const modalContentDesktop = useMemo(
    () => (
      <PkCarousel
        breakpoints={{
          0: {
            slidesPerView: 1.8,
            spaceBetween: 24,
            centeredSlides: true,
          },
        }}
        className={styles.carousel}
        navigationPosition='inside'
        onPkClickPrev={trackSlide('Click', 'Previous image')}
        onPkClickNext={trackSlide('Click', 'Next image')}
        onPkSlidePrev={trackSlide('Swipe', 'Previous image')}
        onPkSlideNext={trackSlide('Swipe', 'Next image')}
      >
        {modalContentMobile}
      </PkCarousel>
    ),
    [modalContentMobile],
  );

  return (
    <Modal
      showModal={showModal}
      handleCloseModal={closeModal}
      showHeader
      hideButtons
      headerTitle={!isMobile ? title : undefined}
      sponsorName={sponsorName}
      className={styles.modal}
      bodyTheme={PkTheme.DARK}
      headerTheme={PkTheme.LIGHT}
    >
      <div className={styles.modalContent} pk-theme={PkTheme.DARK}>
        {isMobile ? modalContentMobile : modalContentDesktop}
      </div>
    </Modal>
  );
};

export default memo(FullScreen);
